import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  IconCheckCircle,
  IconXMarkCircle,
  IconExclamationTriangle,
} from '@/icons';
import { cn } from '@/helpers/classnames';
import { formatScheduledInactivation } from '@/helpers/membershipStatus';

export type TravellerStatus =
  | 'active'
  | 'inactive'
  | 'will-inactivate'
  | 'pending-inactivation';

interface StatusIndicatorProps {
  status: TravellerStatus;
  inactivateAt?: string;
}

export const StatusIndicator = ({
  status,
  inactivateAt,
}: StatusIndicatorProps) => {
  const { formatMessage } = useIntl();

  const statusInfo = useMemo(() => {
    switch (status) {
      case 'active':
        return {
          color: 'text-green-700',
          title: formatMessage({ defaultMessage: 'Aktiv' }),
          icon: IconCheckCircle,
          visible: false,
        };
      case 'inactive':
        return {
          color: 'text-gray-600',
          title: formatMessage({ defaultMessage: 'Inaktiv' }),
          icon: IconXMarkCircle,
          visible: false,
        };
      case 'will-inactivate':
      case 'pending-inactivation':
        return {
          color: 'text-yellow-800',
          title: formatScheduledInactivation(formatMessage, inactivateAt),
          icon: IconExclamationTriangle,
          visible: true,
        };
    }
  }, [formatMessage, inactivateAt, status]);

  return (
    <span className="flex min-w-5 items-center gap-1">
      <statusInfo.icon
        className={cn('size-5', statusInfo.color)}
        aria-hidden="true"
      />
      <span
        className={
          statusInfo.visible ? cn('text-sm', statusInfo.color) : 'sr-only'
        }
      >
        {statusInfo.title}
      </span>
    </span>
  );
};
